import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { confirm } from "../utils/confirm";
import { messaggioErrore } from "../utils/erroriUtente";
import { TIPI_ASSENZA, registraAssenza, annullaAssenza, aggiornaProtocollo, validaAssenza } from "../utils/assenze";
import { parseData } from "../utils/periodoLavorativo";

/** Chi gestisce presenze e assenze degli altri (stessa regola delle Regole Firestore). */
export function useUfficioPersonale() {
  const { userProfile, isPlatformOwner } = useAuth();
  return !!(isPlatformOwner || userProfile?.role === "titolare" || userProfile?.role === "admin" ||
    userProfile?.ruoloOperativo === "gestione" || userProfile?.ruoloOperativo === "amministrazione");
}

const fmt = (s) => { const d = parseData(s); return d ? d.toLocaleDateString("it-IT") : s; };

export function RegistraAssenzaModal({ orgId, uid, nomePersona, periodo, perAltri, onClose, addToast, oreGiornata = 8 }) {
  const { currentUser } = useAuth();
  const oggi = new Date(); const oggiStr = `${oggi.getFullYear()}-${String(oggi.getMonth()+1).padStart(2,"0")}-${String(oggi.getDate()).padStart(2,"0")}`;
  const [tipo, setTipo] = useState("malattia");
  const [dal, setDal] = useState(oggiStr);
  const [al, setAl] = useState(oggiStr);
  const [protocollo, setProtocollo] = useState("");
  const [note, setNote] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [esito, setEsito] = useState(null);
  const t = TIPI_ASSENZA.find(x => x.id === tipo);
  const errore = validaAssenza({ tipo, dal, al });

  const conferma = async () => {
    if (errore || salvando) return;
    setSalvando(true);
    try {
      const r = await registraAssenza({
        orgId, uid, nomePersona, tipo, dal, al, protocollo, note, periodo, oreGiornata,
        autore: { uid: currentUser.uid, nome: currentUser.displayName || currentUser.email || "" },
      });
      setEsito(r);
      addToast?.(r.scritti.length
        ? `${t.label} registrata: ${r.scritti.length} ${r.scritti.length === 1 ? "giornata inserita" : "giornate inserite"} nel cartellino`
        : `${t.label} registrata, ma nessuna giornata da inserire nel cartellino (vedi dettaglio)`, r.scritti.length ? "success" : "info");
    } catch (e) {
      addToast?.(messaggioErrore(e), "error");
    }
    setSalvando(false);
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 440 }}>
        <h3 style={{ fontSize: 15, fontWeight: 800, marginBottom: 4 }}>🩺 Registra assenza{perAltri ? ` — ${nomePersona}` : ""}</h3>
        <div style={{ fontSize: 11.5, color: "#6b7280", marginBottom: 14, lineHeight: 1.5 }}>
          Le giornate feriali del periodo vengono inserite nel cartellino. I giorni già compilati non vengono toccati.
        </div>

        {!esito ? (<>
          <div className="form-group">
            <label className="form-label">Tipo</label>
            <select className="input" value={tipo} onChange={e => setTipo(e.target.value)}>
              {TIPI_ASSENZA.map(x => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Dal</label>
              <input className="input" type="date" value={dal} onChange={e => { setDal(e.target.value); if (al < e.target.value) setAl(e.target.value); }}/>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Al (compreso)</label>
              <input className="input" type="date" value={al} min={dal} onChange={e => setAl(e.target.value)}/>
            </div>
          </div>
          {t?.protocollo && (
            <div className="form-group">
              <label className="form-label">Numero di protocollo del certificato</label>
              <input className="input" value={protocollo} onChange={e => setProtocollo(e.target.value)} placeholder="Es. 123456789 — si può aggiungere anche dopo" inputMode="numeric"/>
              <div style={{ fontSize: 10.5, color: "#9ca3af", marginTop: 4 }}>Nessuna diagnosi: serve solo il numero che il medico comunica. Lo vedono solo l'interessato e l'ufficio del personale.</div>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Note (facoltative)</label>
            <input className="input" value={note} onChange={e => setNote(e.target.value)} maxLength={300}/>
          </div>
          {errore && <div style={{ fontSize: 12, color: "#b91c1c", marginBottom: 8 }}>{errore}</div>}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn" onClick={onClose}>Annulla</button>
            <button className="btn btn-primary" disabled={!!errore || salvando} onClick={conferma}>{salvando ? "Registrazione…" : "Registra assenza"}</button>
          </div>
        </>) : (<>
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            <div>✅ Inserite nel cartellino: <strong>{esito.scritti.length}</strong> {esito.scritti.length === 1 ? "giornata" : "giornate"}.</div>
            {esito.saltati.length > 0 && (
              <div style={{ marginTop: 8, color: "#92400e" }}>
                Non inserite:
                <ul style={{ margin: "4px 0 0 18px" }}>{esito.saltati.map(s => <li key={s.data}>{fmt(s.data)} — {s.motivo}</li>)}</ul>
              </div>
            )}
            {esito.daGestireAMano.length > 0 && (
              <div style={{ marginTop: 8, color: "#b91c1c" }}>
                Cartellino già approvato, da aggiungere a mano: {esito.daGestireAMano.join(", ")}
              </div>
            )}
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
            <button className="btn btn-primary" onClick={onClose}>Chiudi</button>
          </div>
        </>)}
      </div>
    </div>
  );
}

/** Assenze registrate che toccano il mese mostrato, con protocollo e annullamento. */
export function AssenzeDelMese({ orgId, uid, anno, mese0, puoGestire, addToast }) {
  const { currentUser } = useAuth();
  const [assenze, setAssenze] = useState([]);
  useEffect(() => {
    if (!orgId || !uid) return;
    return onSnapshot(query(collection(db, "assenze"), where("organizationId", "==", orgId), where("uid", "==", uid)),
      snap => setAssenze(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      err => console.warn("Assenze non lette:", err));
  }, [orgId, uid]);

  const inizioMese = new Date(anno, mese0, 1), fineMese = new Date(anno, mese0 + 1, 0);
  const delMese = assenze
    .filter(a => a.stato !== "annullata")
    .filter(a => { const d1 = parseData(a.dal), d2 = parseData(a.al); return d1 && d2 && d1 <= fineMese && d2 >= inizioMese; })
    .sort((a, b) => (a.dal || "").localeCompare(b.dal || ""));
  if (!delMese.length) return null;

  const annulla = async (a) => {
    if (!await confirm(`Annullare l'assenza (${a.voce}) dal ${fmt(a.dal)} al ${fmt(a.al)}? Le giornate inserite verranno tolte dal cartellino.`, { type: "danger" })) return;
    try {
      const r = await annullaAssenza({ assenza: a, orgId, autore: { uid: currentUser.uid, nome: currentUser.displayName || "" } });
      addToast?.(r.daGestireAMano.length ? `Assenza annullata — cartellino già approvato: togli a mano ${r.daGestireAMano.join(", ")}` : "Assenza annullata", r.daGestireAMano.length ? "info" : "success");
    } catch (e) { addToast?.(messaggioErrore(e), "error"); }
  };
  const modificaProtocollo = async (a) => {
    const nuovo = window.prompt("Numero di protocollo del certificato:", a.protocollo || "");
    if (nuovo === null) return;
    try { await aggiornaProtocollo(a.id, nuovo); addToast?.("Protocollo aggiornato", "success"); }
    catch (e) { addToast?.(messaggioErrore(e), "error"); }
  };

  return (
    <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, padding: "8px 12px", marginBottom: 10, fontSize: 12 }}>
      <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: 4 }}>🩺 Assenze registrate</div>
      {delMese.map(a => (
        <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "3px 0" }}>
          <span><strong>{a.voce}</strong> dal {fmt(a.dal)} al {fmt(a.al)}</span>
          {a.tipo === "malattia" && <span style={{ color: a.protocollo ? "#6b7280" : "#b91c1c" }}>{a.protocollo ? `prot. ${a.protocollo}` : "protocollo mancante"}</span>}
          {a.perContoDiAltri && <span style={{ color: "#9ca3af" }}>· inserita da {a.inseritaDaNome || "ufficio"}</span>}
          {(puoGestire || a.uid === currentUser?.uid) && (<>
            {a.tipo === "malattia" && <button onClick={() => modificaProtocollo(a)} style={{ fontSize: 11, border: "1px solid #fdba74", background: "#fff", borderRadius: 6, padding: "1px 8px", cursor: "pointer" }}>{a.protocollo ? "Modifica protocollo" : "Aggiungi protocollo"}</button>}
            <button onClick={() => annulla(a)} style={{ fontSize: 11, border: "1px solid #fca5a5", color: "#b91c1c", background: "#fff", borderRadius: 6, padding: "1px 8px", cursor: "pointer" }}>Annulla assenza</button>
          </>)}
        </div>
      ))}
    </div>
  );
}
